












import Vue from "vue";
import {
  ref,
  computed,
  onMounted,
  onBeforeUnmount,
  watch
} from "@vue/composition-api";
import { onTouchOutsideOnce, onKey } from "@/common/dom.functions";
import { emptyFn } from "../fp";
import { EventBus } from "../eventbus";
enum PopupPosition {
  LEFT_TOP = "left-top",
  RIGHT_TOP = "right-top",
  BOTTOM_LEFT = "bottom-left",
  BOTTOM_RIGHT = "bottom-right",
  BOTTOM_CENTER = "bottom-center",
  CENTER_LEFT = "center-left",
  MANUAL = "manual"
}
export default Vue.extend({
  props: {
    position: {
      type: String,
      default: "manual"
    },
    target: {
      type: String,
      default: ""
    },
    ignoreEls: {
      type: Array,
      default: () => []
    },
    closeInside: {
      type: Boolean,
      default: false
    },
    display: {
      type: Boolean,
      default: false
    },
    passive: {
      type: Boolean,
      default: false
    }
  },
  setup(props, { emit }) {
    const rootEl = () => document.querySelector("#popup-root") as Element;
    let targetEl: () => Element[] = () => [];
    const show = ref(false);

    const onTouchOutsidePopup = onTouchOutsideOnce(() => {
      show.value = false;
      emit("cancel");
    }, props.closeInside as boolean);

    let onTouchOutsidePopupSubscription = emptyFn();

    const onTargetClick = () => {
      if (!show.value) {
        onTouchOutsidePopupSubscription = onTouchOutsidePopup(rootEl, targetEl);
      }
      show.value = !show.value;
    };

    let escSubscriber = emptyFn();
    const onEsc = onKey("Escape", false);

    watch(show, (newVal: boolean) => {
      emit(newVal ? "open" : "close");
      if (show.value) {
        escSubscriber();
        escSubscriber = onEsc(() => {
          close();
          emit("cancel");
        });
      }
    });

    const _ignoreEls = () =>
      (props.ignoreEls as string[])
        .map(selector => Array.from(document.querySelectorAll(selector)))
        .flat();

    const setHasPassive = () => {
      const _display = computed(() => props.display as boolean);
      watch(_display, (newVal: boolean) => {
        show.value = newVal;
        if (show.value) {
          targetEl = () => [..._ignoreEls()];
          onTouchOutsidePopupSubscription = onTouchOutsidePopup(
            rootEl,
            targetEl
          );
        }
      });
    };

    /**
     * @public
     *  */
    const close = (emitEvent = true) => {
      onTouchOutsidePopupSubscription && onTouchOutsidePopupSubscription();

      show.value = false;
    };

    const popupCloseEventBusSub = EventBus.listen("GLOBAL-popups-close", () => {
      close();
    });
    onMounted(() => {
      if (props.passive) {
        setHasPassive();
      }
      if (props.target) {
        targetEl = () => [
          document.querySelector("#" + props.target) as Element,
          ..._ignoreEls()
        ];

        targetEl()
          .filter(el => el)
          .forEach(el => el.addEventListener("click", onTargetClick));
      }
    });

    onBeforeUnmount(() => {
      popupCloseEventBusSub();
      escSubscriber();
      targetEl()
        .filter(f => f)
        .forEach(el => el.removeEventListener("click", onTargetClick));
      onTouchOutsidePopupSubscription && onTouchOutsidePopupSubscription();
    });

    const positions = computed(() => {
      switch (props.position) {
        case PopupPosition.LEFT_TOP:
          return "left-0 top-0 -translate-x-full";
        case PopupPosition.RIGHT_TOP:
          return "right-0 top-0 translate-x-full";
        case PopupPosition.BOTTOM_RIGHT:
          return "bottom-0 right-0 translate-y-full";
        case PopupPosition.BOTTOM_LEFT:
          return "bottom-0 left-0 translate-y-full";
        case PopupPosition.BOTTOM_CENTER:
          return "bottom-0 left-0 translate-y-full -translate-x-1/2";
        case PopupPosition.CENTER_LEFT:
          return "bottom-0 left-0 translate-y-1/2 -translate-x-full";
        case PopupPosition.MANUAL:
        default:
          return "";
      }
    });

    return {
      show,
      positions,
      close
    };
  }
});
